
import { ErrorContext } from '@/types/common';
import { Sentry, isSentryEnabled } from '@/sentry';

export class AppError extends Error {
  public readonly code: string;
  public readonly context?: ErrorContext;
  public readonly isOperational: boolean;

  constructor(
    message: string,
    code: string = 'UNKNOWN_ERROR',
    context?: ErrorContext,
    isOperational: boolean = true
  ) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.context = context;
    this.isOperational = isOperational;

    // Ensure proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, AppError.prototype);
  }
}

export class ValidationError extends AppError {
  public readonly validationErrors: any[];

  constructor(
    message: string,
    validationErrors: any[] = [],
    context?: ErrorContext
  ) {
    super(message, 'VALIDATION_ERROR', context);
    this.name = 'ValidationError';
    this.validationErrors = validationErrors;
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

export class APIError extends AppError {
  public readonly statusCode: number;

  constructor(
    message: string,
    statusCode: number = 500,
    context?: ErrorContext
  ) {
    super(message, `API_ERROR_${statusCode}`, context);
    this.name = 'APIError';
    this.statusCode = statusCode;
    Object.setPrototypeOf(this, APIError.prototype);
  }
}

export const handleError = (error: Error, context: ErrorContext) => {
  const errorInfo = {
    message: error.message,
    stack: error.stack,
    context,
    timestamp: new Date().toISOString(),
  };

  // Log error
  console.error('Application error:', errorInfo);

  if (isSentryEnabled) {
    Sentry.withScope((scope) => {
      scope.setTag('component', context.component);
      scope.setTag('action', context.action);
      if (context.userId) {
        scope.setUser({ id: context.userId });
      }
      if (context.additionalInfo) {
        scope.setExtra('additionalInfo', context.additionalInfo);
      }
      scope.setExtra('timestamp', errorInfo.timestamp);
      Sentry.logger.error('Application error', {
        code: error instanceof AppError ? error.code : 'UNKNOWN_ERROR',
        component: context.component,
        action: context.action,
        ...(context.userId ? { userId: context.userId } : {}),
      });
      Sentry.metrics.count('app.error', 1, {
        attributes: {
          code: error instanceof AppError ? error.code : 'UNKNOWN_ERROR',
          component: context.component,
          action: context.action,
        },
      });
      Sentry.captureException(error);
    });
  }

  return errorInfo;
};

export const createErrorHandler = (component: string) => {
  return (error: Error, action: string, additionalInfo?: Record<string, any>) => {
    const context: ErrorContext = {
      component,
      action,
      additionalInfo,
    };
    return handleError(error, context);
  };
};

// Export types
export type { ErrorContext };
